import { UtensilsCrossed, Factory, LayoutGrid, LifeBuoy, Users, MessageSquareQuote, Handshake, Puzzle, RotateCw, Grip } from "lucide-react";
import { localizePath } from "@/routes/localizedPath";
import type { Lang } from "@/routes/routeMap";

// Helper: localize a path for the active lang. Hashes (#…) on the same page
// stay verbatim. Paths like "/spare-parts#controllers" are split into the
// route part and the hash, the route part is localized, and the hash is appended.
const L = (path: string, lang: Lang): string => localizePath(path, lang);

export const getOfferItems = (t: any, lang: Lang) => [
  { label: t.offerGastroMedicine, href: L("/solutions/gastronomy-medicine", lang), icon: UtensilsCrossed, subItems: [
    { label: t.solGastSinks, href: L("/solutions/spot-welding-sinks", lang) },
  ]},
  { label: t.offerProducts, href: L("/products", lang), icon: Factory, subItems: [
    { label: t.prodSpotWeldingCategory, href: L("/products/spot-welding", lang), isCategory: true, subItems: [
      { label: lang === "en" ? "Rocker Arm Welders" : "Stroji s škarjastim hodom", href: L("/products/spot-welding", lang), isCategory: true, subItems: [
        { label: "RS-81 / RP-81 MFDC", href: L("/products/spot-welding/rs-81", lang) },
        { label: "RB-81", href: L("/products/spot-welding/rb-81", lang) },
      ]},
      { label: lang === "en" ? "Linear Action Welders RT-94" : "Stroji z linearnim hodom RT-94", href: L("/products/spot-welding/rt-94", lang) },
      { label: lang === "en" ? "Bench Welders RP-84" : "Namizni stroji RP-84", href: L("/products/spot-welding/rp-84", lang) },
      { label: lang === "en" ? "Industrial Welding Guns" : "Industrijske klešče za varjenje", href: L("/products/spot-welding", lang), isCategory: true, subItems: [
        { label: "RP-96-X", href: L("/products/spot-welding/rp-96-x", lang) },
        { label: "RP-96-C", href: L("/products/spot-welding/rp-96-c", lang) },
      ]},

      { label: lang === "en" ? "Twin Spot Welders" : "Dvojni točkovni varilniki", href: L("/products/spot-welding", lang), isCategory: true, subItems: [
        { label: "RGS", href: L("/products/spot-welding/rgs", lang) },
        { label: "RDR", href: L("/products/spot-welding/rdr", lang) },
      ]},
      { label: lang === "en" ? "Hand Held Welding Gun" : "Ročni varilni točkalnik", href: L("/products/spot-welding/hand-held-welding-gun", lang) },
    ]},
    { label: lang === "en" ? "Projection Welding RB-94" : "Bradavičasto varjenje RB-94", href: L("/products/projection-welding", lang), isCategory: true, subItems: [] },
    { label: lang === "en" ? "Seam Welders" : "Kolutno varjenje", isCategory: true, subItems: [
      { label: "RK-94", href: L("/products/seam-welding/rk-94", lang) },
    ] },
    { label: lang === "en" ? "Butt Welder RP-97" : "Stroj za sočelno varjenje RP-97", href: L("/products/butt-welding", lang), isCategory: true, subItems: [] },
    { label: t.prodMicroWelding, href: L("/products/micro-welding", lang), isCategory: true, subItems: [
      { label: lang === "en" ? "Servo Welding Heads" : "Servo varilne glave", href: L("/products/servo-welding-heads", lang) },
      { label: lang === "en" ? "Double Welding Heads" : "Dvojne varilne glave", href: L("/products/double-welding-heads", lang) },
      { label: t.prodWeldingHeads, href: L("/products/welding-heads", lang) },
      { label: t.prodWeldingPincers, href: L("/products/welding-pincers", lang) },
      { label: t.prodCompactingTools, href: L("/solutions/compacting-tools", lang) },
      { label: t.prodBatteryProcessing, href: L("/products/battery-processing", lang) },
      { label: t.prodWorkstations, href: L("/products/workstation", lang) },
      { label: lang === "en" ? "IC Bending Tool" : "Orodje za upogibanje IC", href: L("/products/ic-bending-tool", lang) },
      { label: lang === "en" ? "Welding Controls" : "Varilni krmilniki", href: L("/products/welding-controls", lang) },
    ]},
    { label: lang === "en" ? "Car Spotters" : "Varilni program za avtoličarstvo", href: L("/products/car-spotters", lang), isCategory: true, subItems: [
      { label: "Car Point", href: L("/products/spot-welding/car-point", lang) },
      { label: "Europei", href: L("/products/spot-welding/europei", lang) },
      { label: "MFDC Welding Unit", href: L("/products/spot-welding/mfdc-welding-unit", lang) },
      { label: "RP-84/3,5-ECO", href: L("/products/spot-welding/fast-spotter", lang) },
    ]},
  ]},
  { label: t.offerSpecialMachines, href: L("/solutions", lang), icon: LayoutGrid, subItems: [
    { label: t.prodPedestalWelders, href: L("/products/industrial-pedestal-welders", lang) },
    { label: t.prodProjectionWelders, href: L("/products/industrial-projection-welders", lang) },
    { label: t.prodSeamWelders, href: L("/products/industrial-seam-welders", lang) },
    { label: t.prodMultiSpot, href: L("/products/multi-spot-welding-machine", lang) },
    { label: t.solRoboticSpotGuns, href: L("/solutions/robotic-spot-welding-guns", lang) },
    { label: t.solLocksWelding, href: L("/solutions/lock-welding", lang) },
    { label: t.solTensionRingWelding, href: L("/solutions/tension-ring-welding", lang) },
    { label: t.solBarrelHandleWelding, href: L("/solutions/barrel-handle-welding", lang) },
    { label: t.solTappingBarrelWelding, href: L("/solutions/tapping-barrel-welding", lang) },
    { label: t.solProtectiveMeshWelding, href: L("/solutions/protective-mesh-welding", lang) },
    { label: t.solOilTankWelding, href: L("/solutions/oil-tank-welding", lang) },
    { label: t.solDoubleSidedSeamOvenWelding, href: L("/solutions/double-sided-seam-oven-welding", lang) },
    { label: t.solChimneyElbowWelding, href: L("/solutions/chimney-elbow-welding", lang) },
    { label: t.solWeldDestructiveTest, href: L("/solutions/weld-destructive-test", lang) },
    { label: t.solAluminumFrameWelding, href: L("/solutions/aluminum-frame-welding", lang) },
    { label: t.solDoorProfileSeamWelding, href: L("/solutions/door-profile-seam-welding", lang) },
    { label: t.solNutWelding, href: L("/solutions/nut-welding", lang) },
  ] },
  { label: lang === "en" ? "Spare Parts & Components" : "Rezervni deli in komponente", href: L("/spare-parts", lang), icon: Puzzle, subItems: [
    { label: lang === "en" ? "Seam Welding Heads" : "Kolutne glave", href: L("/spare-parts", lang) + "#seam-wheels", isCategory: true, subItems: [] },
    { label: lang === "en" ? "Transformers" : "Transformatorji", href: L("/spare-parts/transformers", lang), isCategory: true, subItems: [
      { label: lang === "en" ? "Three-Phase Rectifier (TGE)" : "Trifazne usmerniške enote (TGE)", href: L("/spare-parts/transformers", lang) + "#three-phase-rectifier" },
      { label: lang === "en" ? "Capacitor Discharge / Pulse" : "Pulzni / KE varjenje", href: L("/spare-parts/transformers", lang) + "#capacitor-discharge" },
    ]},
    { label: lang === "en" ? "Welding Components" : "Varilne komponente", href: L("/spare-parts", lang) + "#controllers", isCategory: true, subItems: [
      { label: "Bosch Rexroth", href: L("/spare-parts/bosch-rexroth", lang) },
      { label: "Harms & Wende", href: L("/spare-parts/harms-wende", lang) },
    ]},
  ]},
  { label: lang === "en" ? "Consumables" : "Potrošni material", href: L("/consumables", lang), icon: RotateCw, subItems: [
    { label: lang === "en" ? "Electrodes" : "Elektrode", href: L("/consumables", lang) + "#electrodes" },
    { label: lang === "en" ? "Ceramic Pins" : "Keramični pini", href: L("/consumables", lang) + "#ceramic-pins" },
  ]},
  { label: lang === "en" ? "Accessories" : "Dodatna oprema", href: L("/accessories", lang), icon: Grip, subItems: [
    { label: lang === "en" ? "Tip Dressers" : "Posnemovalci kapic", href: L("/accessories/tip-dressers", lang) },
  ]},
  { label: t.offerServices, href: L("/services", lang), icon: LifeBuoy, subItems: [] },
];

export const getTrustItems = (t: any, lang: Lang) => ({
  reference: [
    { label: t.refUsers, href: L("/users-clients", lang), icon: Users },
  ],
  testimonials: [
    { label: t.refTestimonials, href: L("/testimonials", lang), icon: MessageSquareQuote },
  ],
  partners: [
    { label: t.refPartners, href: L("/partners", lang), icon: Handshake },
  ],
});

export const getNavItems = (t: any, lang: Lang) => [
  { label: t.navHome, href: L("/", lang) },
  { label: t.navAbout, href: L("/about-us", lang), isPage: true },
  { label: t.navServices, href: L("/", lang) + "#services", dropdown: "offer" as const },
  { label: t.navReferences, href: L("/", lang) + "#references", dropdown: "trust" as const },
  { label: t.navKnowledge, href: L("/knowledge-hub", lang) },
  { label: t.navFAQ, href: L("/faq", lang) },
  { label: t.navContact, href: L("/", lang) + "#contact" },
];
